import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchHintBank, listHintBanks, saveHintBank } from "../../hints/hintApi";
import { listHintBanks as bundledBanks, getHintBank } from "../../hints/hintBank";

// HintEditor is the "Hints" admin tab (SCRUM-686): one room at a time, its riddles in the order
// players meet them, three hint levels per riddle (DA + EN), expected minutes and chat keywords,
// plus the room's situational hints. The whole bank is saved as one document with the version
// it was loaded at; a stale save (someone else saved meanwhile) is refused and reloaded.
//
// Rooms that have no bank in games-cloud yet can be started from the bank bundled in the image.

const LEVEL_LABEL = { 1: "Niveau 1: et skub", 2: "Niveau 2: mere hjælp", 3: "Niveau 3: nødudgangen" };

function slug(s) {
    return s.toLowerCase()
        .replace(/æ/g, "ae").replace(/ø/g, "oe").replace(/å/g, "aa")
        .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

// autoGrow sizes a one-line textarea to its content, so a short hint stays one row high and a
// long one shows in full. Used as a ref callback (initial size) and from onChange.
function autoGrow(el) {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
}

function emptyRiddle() {
    return {
        id: "", title: "", expectedMin: 5, keywords: [],
        hints: [{ level: 1, text: "", en: "" }, { level: 2, text: "", en: "" }, { level: 3, text: "", en: "" }],
    };
}

// normalise makes an editor-friendly copy: every riddle has exactly levels 1..3, keywords is an array.
function normalise(bank) {
    return {
        ...bank,
        linear: !!bank.linear,
        enabled: bank.enabled !== false,
        situational: Array.isArray(bank.situational) ? bank.situational.map(s => ({ label: s.label || "", text: s.text || "", en: s.en || "" })) : [],
        riddles: (bank.riddles || []).map(r => {
            const byLevel = {};
            (r.hints || []).forEach(h => { byLevel[h.level] = h; });
            return {
                ...r,
                keywords: Array.isArray(r.keywords) ? r.keywords : [],
                hints: [1, 2, 3].map(l => ({ level: l, text: byLevel[l]?.text || "", en: byLevel[l]?.en || "" })),
            };
        }),
    };
}

// forSave drops empty hint levels and tidies fields; what games-cloud validates is id + title.
function forSave(bank) {
    return {
        ...bank,
        riddles: bank.riddles.map(r => ({
            ...r,
            id: r.id.trim(),
            title: r.title.trim(),
            expectedMin: Number(r.expectedMin) || 0,
            keywords: r.keywords.map(k => k.trim()).filter(Boolean),
            hints: r.hints.filter(h => h.text.trim()).map(h => ({ level: h.level, text: h.text.trim(), ...(h.en?.trim() ? { en: h.en.trim() } : {}) })),
        })),
        situational: bank.situational.filter(s => s.text.trim()).map(s => ({ label: s.label.trim(), text: s.text.trim(), ...(s.en?.trim() ? { en: s.en.trim() } : {}) })),
    };
}

function validate(bank) {
    const errs = [];
    if (!bank.riddles.length) errs.push("Rummet skal have mindst én gåde.");
    const seen = new Set();
    bank.riddles.forEach((r, i) => {
        const n = i + 1;
        if (!r.id.trim()) errs.push(`Gåde ${n}: mangler id.`);
        else if (seen.has(r.id.trim())) errs.push(`Gåde ${n}: id "${r.id}" bruges to gange.`);
        seen.add(r.id.trim());
        if (!r.title.trim()) errs.push(`Gåde ${n}: mangler titel.`);
        if (!r.hints.some(h => h.text.trim())) errs.push(`Gåde ${n} (${r.title || r.id}): ingen hints.`);
    });
    return errs;
}

export default function HintEditor() {
    const [rooms, setRooms] = useState([]);          // from games-cloud
    const [selected, setSelected] = useState("");    // gameId
    const [version, setVersion] = useState(0);
    const [bank, setBank] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [status, setStatus] = useState(null);      // {kind: "ok"|"error", text}
    const [busy, setBusy] = useState(false);
    const [openRiddle, setOpenRiddle] = useState(null);

    const refreshRooms = useCallback(async () => {
        try { setRooms(await listHintBanks()); }
        catch (e) { setStatus({ kind: "error", text: `Kunne ikke hente rum: ${e.message}` }); }
    }, []);
    useEffect(() => { refreshRooms(); }, [refreshRooms]);

    // loadSeq makes a slow fetch for a room the admin has already moved away from a no-op, so
    // it can never land its bank under the newer selection.
    const loadSeq = useRef(0);
    const load = useCallback(async (gameId) => {
        const seq = ++loadSeq.current;
        setSelected(gameId); setBank(null); setStatus(null); setDirty(false); setOpenRiddle(null);
        if (!gameId) return;
        try {
            const row = await fetchHintBank(gameId);
            if (seq !== loadSeq.current) return;
            if (row) { setBank(normalise(row.bank)); setVersion(row.version); return; }
            const bundled = getHintBank(gameId);
            if (bundled) {
                setBank(normalise(bundled)); setVersion(0); setDirty(true);
                setStatus({ kind: "ok", text: "Rummet har ingen bank i systemet endnu. Det her er udkastet fra appen; tryk Gem for at oprette den." });
            } else {
                setBank(normalise({ gameId, name: gameId, linear: true, riddles: [emptyRiddle()], situational: [] }));
                setVersion(0); setDirty(true);
            }
        } catch (e) {
            if (seq !== loadSeq.current) return;
            setStatus({ kind: "error", text: `Kunne ikke hente banken: ${e.message}` });
        }
    }, []);

    const update = (fn) => { setBank(prev => fn(prev)); setDirty(true); };
    const updateRiddle = (i, patch) => update(b => ({ ...b, riddles: b.riddles.map((r, j) => j === i ? { ...r, ...patch } : r) }));
    const updateHint = (i, level, patch) => update(b => ({
        ...b,
        riddles: b.riddles.map((r, j) => j === i ? { ...r, hints: r.hints.map(h => h.level === level ? { ...h, ...patch } : h) } : r),
    }));
    const moveRiddle = (i, dir) => update(b => {
        const j = i + dir;
        if (j < 0 || j >= b.riddles.length) return b;
        const rs = [...b.riddles];
        [rs[i], rs[j]] = [rs[j], rs[i]];
        return { ...b, riddles: rs };
    });
    const addRiddle = () => { update(b => ({ ...b, riddles: [...b.riddles, emptyRiddle()] })); setOpenRiddle(bank.riddles.length); };
    const removeRiddle = (i) => {
        if (!window.confirm(`Slet gåden "${bank.riddles[i].title || bank.riddles[i].id}"?`)) return;
        update(b => ({ ...b, riddles: b.riddles.filter((_, j) => j !== i) }));
        setOpenRiddle(null);
    };

    const save = async () => {
        const errs = validate(bank);
        if (errs.length) { setStatus({ kind: "error", text: errs.join(" ") }); return; }
        setBusy(true); setStatus(null);
        try {
            const saved = await saveHintBank(selected, version, forSave(bank));
            setVersion(saved.version); setDirty(false);
            setStatus({ kind: "ok", text: `Gemt (version ${saved.version}).` });
            refreshRooms();
        } catch (e) {
            if (e.status === 409) {
                setStatus({ kind: "error", text: "En anden har gemt rummet imens. Dine ændringer er IKKE gemt. Kopiér det, du vil beholde, og genindlæs rummet." });
            } else {
                setStatus({ kind: "error", text: `Kunne ikke gemme: ${e.message}` });
            }
        } finally { setBusy(false); }
    };

    // Rooms to offer: everything games-cloud has, plus bundled banks it does not have yet.
    const known = new Set(rooms.map(r => r.gameId));
    const options = [
        ...rooms.map(r => ({ gameId: r.gameId, label: `${r.name || r.gameId} (${r.riddles} gåder, v${r.version})` })),
        ...bundledBanks().filter(b => !known.has(b.gameId)).map(b => ({ gameId: b.gameId, label: `${b.name} (ikke i systemet endnu)` })),
    ];

    return (
        <div className="hint-editor">
            <div className="hint-editor-top">
                <label>
                    Rum
                    <select value={selected} onChange={e => { if (!dirty || window.confirm("Du har ugemte ændringer. Skift rum alligevel?")) load(e.target.value); }}>
                        <option value="">Vælg rum…</option>
                        {options.map(o => <option key={o.gameId} value={o.gameId}>{o.label}</option>)}
                    </select>
                </label>
                {bank && (
                    <div className="hint-editor-actions">
                        <span className="acl-dim">{dirty ? "Ugemte ændringer" : `Version ${version}`}</span>
                        <button className="accent" disabled={busy || !dirty} onClick={save}>{busy ? "Gemmer…" : "Gem"}</button>
                        <button disabled={busy} onClick={() => { if (!dirty || window.confirm("Smid ændringerne væk?")) load(selected); }}>Genindlæs</button>
                    </div>
                )}
            </div>

            {status && <div className={status.kind === "ok" ? "acl-ok" : "acl-error"}>{status.text}</div>}

            {bank && (
                <>
                    <div className="hint-editor-room acl-card">
                        <div className="acl-fields">
                            <label>Navn <input value={bank.name || ""} onChange={e => update(b => ({ ...b, name: e.target.value }))} /></label>
                            <label className="hint-editor-check">
                                <input type="checkbox" checked={!!bank.linear} onChange={e => update(b => ({ ...b, linear: e.target.checked }))} />
                                Lineært rum (gåderne løses i rækkefølge)
                            </label>
                            <label className="hint-editor-check hint-editor-enabled">
                                <input type="checkbox" checked={!!bank.enabled} onChange={e => update(b => ({ ...b, enabled: e.target.checked }))} />
                                {bank.enabled ? "Aktiv: vises i HUD'en" : "Ikke aktiv: skjult i HUD'en"}
                            </label>
                        </div>
                        <p className="acl-hint">
                            Panelet vises kun, når rummet er aktivt her, og brugeren har flueben i kolonnen "Hints" i medarbejderarket.
                        </p>
                        <p className="acl-hint">
                            Lineært betyder, at et hint om gåde 5 fortæller HUD'en, at 1 til 4 er løst. "Plejer at tage" er, hvor mange
                            minutter et typisk hold bruger på gåden; HUD'en bruger det til at gætte, hvor holdet er, når intet andet siger det.
                        </p>
                    </div>

                    <h3 className="hint-editor-h">Gåder i rækkefølge</h3>
                    {bank.riddles.map((r, i) => {
                        const open = openRiddle === i;
                        return (
                            <div key={i} className={`acl-card hint-riddle ${open ? "is-open" : ""}`}>
                                <div className="acl-card-head hint-riddle-head" onClick={() => setOpenRiddle(open ? null : i)}>
                                    <span className="hint-riddle-no">{i + 1}</span>
                                    <span className="hint-riddle-title">{r.title || <em>uden titel</em>}</span>
                                    <span className="acl-dim">{r.id || "intet id"} · {r.expectedMin || 0} min · {r.hints.filter(h => h.text.trim()).length} hints</span>
                                    <span className="acl-row-actions" onClick={e => e.stopPropagation()}>
                                        <button title="Flyt op" disabled={i === 0} onClick={() => moveRiddle(i, -1)}>↑</button>
                                        <button title="Flyt ned" disabled={i === bank.riddles.length - 1} onClick={() => moveRiddle(i, 1)}>↓</button>
                                        <button className="hint-del" title="Slet gåden" onClick={() => removeRiddle(i)}>✕</button>
                                    </span>
                                </div>
                                {open && (
                                    <div className="hint-riddle-body">
                                        <div className="acl-fields">
                                            <label>Titel
                                                <input value={r.title} onChange={e => updateRiddle(i, { title: e.target.value, ...(r.id ? {} : { id: slug(e.target.value) }) })} />
                                            </label>
                                            <label>Id
                                                <input value={r.id} onChange={e => updateRiddle(i, { id: slug(e.target.value) || e.target.value })} />
                                            </label>
                                            <label>Plejer at tage (min)
                                                <input type="number" min="0" value={r.expectedMin} onChange={e => updateRiddle(i, { expectedMin: e.target.value })} />
                                            </label>
                                            <label>Nøgleord (komma-adskilt)
                                                <input value={r.keywords.join(", ")} onChange={e => updateRiddle(i, { keywords: e.target.value.split(",") })} />
                                            </label>
                                        </div>
                                        <p className="acl-hint">Id'et er nøglen i systemet. I rum med sensorer skal det være det navn, game-controlleren melder, når gåden er løst. Nøgleord er, hvad spillerne skriver i chatten om gåden.</p>
                                        {r.hints.map(h => (
                                            <div key={h.level} className="hint-level-row">
                                                <div className="hint-level-label">{LEVEL_LABEL[h.level]}</div>
                                                <textarea rows={4} placeholder="Dansk" value={h.text} onChange={e => updateHint(i, h.level, { text: e.target.value })} />
                                                <textarea rows={4} placeholder="English" value={h.en} onChange={e => updateHint(i, h.level, { en: e.target.value })} />
                                            </div>
                                        ))}
                                        {Array.isArray(r.alternatives) && r.alternatives.length > 0 && (
                                            <details className="hint-alts">
                                                <summary>Alternative formuleringer fra guiden ({r.alternatives.length})</summary>
                                                <ul>{r.alternatives.map((a, k) => <li key={k}>{a}</li>)}</ul>
                                            </details>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <button onClick={addRiddle}>+ Tilføj gåde</button>

                    <h3 className="hint-editor-h">Hints, der ikke hører til en gåde</h3>
                    <p className="acl-hint">Situationshints: kodepanelet, "noget har åbnet sig", nødudgangen ved tidsnød. GM'en vælger dem selv.</p>
                    {bank.situational.map((s, i) => (
                        <div key={i} className="acl-card hint-riddle hint-situ-row">
                            <input placeholder="Situation" value={s.label} onChange={e => update(b => ({ ...b, situational: b.situational.map((x, j) => j === i ? { ...x, label: e.target.value } : x) }))} />
                            <textarea rows={1} placeholder="Dansk" value={s.text} ref={autoGrow}
                                onChange={e => { autoGrow(e.target); update(b => ({ ...b, situational: b.situational.map((x, j) => j === i ? { ...x, text: e.target.value } : x) })); }} />
                            <textarea rows={1} placeholder="English" value={s.en} ref={autoGrow}
                                onChange={e => { autoGrow(e.target); update(b => ({ ...b, situational: b.situational.map((x, j) => j === i ? { ...x, en: e.target.value } : x) })); }} />
                            <button className="hint-del" title="Slet" onClick={() => update(b => ({ ...b, situational: b.situational.filter((_, j) => j !== i) }))}>✕</button>
                        </div>
                    ))}
                    <button onClick={() => update(b => ({ ...b, situational: [...b.situational, { label: "", text: "", en: "" }] }))}>+ Tilføj situationshint</button>
                </>
            )}
        </div>
    );
}
